import {createRouter, createWebHistory} from 'vue-router';

function getVueBasePath() {
    const fullPath = window.location.pathname;
    const match = fullPath.match(/(.*\/customer-portal)(\/|$)/);
    return match ? match[1] + '/' : '/';
}

const basePath = getVueBasePath();

export enum CPRouterPaths {
    indexPage = '/customer-portal',
    homePage = '/',
    profilePage = '/profile',
    editProfilePage = '/edit-profile',
    changePasswordPage = '/change-password',
    quotesPage = '/quotes',
    singleQuotePage = '/quotes/:quoteUid',
    shareQuotePage = '/quotes/share/:quoteType/:quoteHash/:quoteUid',
    createHTCHQuotePage = '/create-htct-quote',
    companyInfoPage = '/company-info',
    createAddressPage = '/create-address',
    editAddressPage = '/edit-address',
    authPage = '/auth',
}

export enum CPTutQuoteRouterPaths {
    indexPage = '/tutquote',
    homePage = '',
    quotesPage = 'quotes',
    createQuotePage = 'quotes/create',
    quoteDetailPage = 'quotes/:quoteUid',
    editQuotePage = 'quotes/:quoteUid/edit',
    quoteOrderPage = 'quotes/:quoteUid/order',
    createQuoteItemPage = 'quotes/:quoteUid/items/create',
    editQuoteItemPage = 'quotes/:quoteUid/items/:itemUid/edit',
    usersPage = 'users',
    poLookupPage = 'po-lookup',
    helpPage = 'help',
}

const customerPortalRoutes = [
    {
        path: CPRouterPaths.homePage,
        component: () => import('@/apps/website/customer-portal/views/CPHomeView.vue'),
        meta: {breadcrumb: 'Home'},
    },
    {
        path: CPRouterPaths.profilePage,
        component: () => import('@/apps/website/customer-portal/views/CPProfileView.vue'),
        meta: {breadcrumb: 'My Profile'},
    },
    {
        path: CPRouterPaths.editProfilePage,
        component: () => import('@/apps/website/customer-portal/views/CPEditProfileView.vue'),
        meta: {breadcrumb: 'Edit profile', parent: CPRouterPaths.profilePage},
    },
    {
        path: CPRouterPaths.changePasswordPage,
        component: () => import('@/apps/website/customer-portal/views/CPChangePasswordView.vue'),
        meta: {breadcrumb: 'Change password', parent: CPRouterPaths.profilePage},
    },
    {
        path: CPRouterPaths.quotesPage,
        component: () => import('@/apps/website/customer-portal/views/CPQuotesView.vue'),
        meta: {breadcrumb: 'HTCH Quotes'},
    },
    {
        path: CPRouterPaths.singleQuotePage,
        component: () => import('@/apps/website/customer-portal/views/CPSingleQuoteView.vue'),
        meta: {breadcrumb: 'Quote Details', parent: CPRouterPaths.quotesPage},
    },
    {
        path: CPRouterPaths.shareQuotePage,
        component: () => import('@/apps/website/customer-portal/views/CPShareQuoteView.vue'),
        meta: {breadcrumb: 'Quote Details for Sharing', parent: CPRouterPaths.quotesPage},
    },
    {
        path: CPRouterPaths.createHTCHQuotePage,
        component: () => import('@/apps/website/customer-portal/views/CPCreateHTCHQuoteView.vue'),
        meta: {
            breadcrumb: (route: any) => route.query['edit-quote-item-uid'] ? 'Edit HTCH quote item' : route.query['quote-uid'] ? 'Add item to HTCH quote' : 'Create HTCH quote',
            parent: CPRouterPaths.quotesPage,
        },
    },
    {
        path: CPRouterPaths.companyInfoPage,
        component: () => import('@/apps/website/customer-portal/views/CPCompanyInfoView.vue'),
        meta: {breadcrumb: 'My Company Info'},
    },
    {
        path: CPRouterPaths.createAddressPage,
        component: () => import('@/apps/website/customer-portal/views/CPCreateAddressView.vue'),
        meta: {breadcrumb: 'Create address', parent: CPRouterPaths.companyInfoPage},
    },
    {
        path: CPRouterPaths.editAddressPage,
        component: () => import('@/apps/website/customer-portal/views/CPEditAddressView.vue'),
        meta: {breadcrumb: 'Edit address', parent: CPRouterPaths.companyInfoPage},
    },
    {
        path: CPRouterPaths.authPage,
        component: () => import('@/apps/website/customer-portal/views/CPAuthView.vue'),
        meta: {breadcrumb: 'Authentication'},
    },
    {
        path: CPTutQuoteRouterPaths.indexPage,
        component: () => import('@/apps/website/tutquote/TutQuoteVue3App.vue'),
        meta: {breadcrumb: 'TutQuotes'},
        children: [
            {
                path: CPTutQuoteRouterPaths.homePage,
                name: 'home',
                component: () => import('@/apps/website/tutquote/views/TutQuoteHomeView.vue'),
                meta: {breadcrumb: 'Weekly Data', parent: CPTutQuoteRouterPaths.indexPage},
            },
            {
                path: CPTutQuoteRouterPaths.quotesPage,
                name: 'quote-list',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuotesListView.vue'),
                meta: {breadcrumb: 'Quotes', parent: CPTutQuoteRouterPaths.indexPage},
            },
            {
                path: CPTutQuoteRouterPaths.createQuotePage,
                name: 'quote-edit',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteEditView.vue'),
                meta: {
                    breadcrumb: 'New Quote',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quotesPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.quoteDetailPage,
                name: 'quote-detail',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteDetailView.vue'),
                meta: {
                    breadcrumb: 'Quote Details',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quotesPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.editQuotePage,
                name: 'quote-edit-existing',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteEditView.vue'),
                meta: {
                    breadcrumb: 'Edit Quote',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quoteDetailPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.quoteOrderPage,
                name: 'quote-order',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteOrderView.vue'),
                meta: {
                    breadcrumb: 'Place Order',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quoteDetailPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.createQuoteItemPage,
                name: 'quote-item-create',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteItemEditView.vue'),
                meta: {
                    breadcrumb: 'New Item',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quoteDetailPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.editQuoteItemPage,
                name: 'quote-item-edit',
                component: () => import('@/apps/website/tutquote/views/TutQuoteQuoteItemEditView.vue'),
                meta: {
                    breadcrumb: 'Edit Item',
                    parent: `${CPTutQuoteRouterPaths.indexPage}/${CPTutQuoteRouterPaths.quoteDetailPage}`,
                },
            },
            {
                path: CPTutQuoteRouterPaths.usersPage,
                name: 'user-list',
                component: () => import('@/apps/website/tutquote/views/TutQuoteUserListView.vue'),
                meta: {breadcrumb: 'Users', parent: CPTutQuoteRouterPaths.indexPage},
            },
            {
                path: CPTutQuoteRouterPaths.poLookupPage,
                name: 'po-lookup',
                component: () => import('@/apps/website/tutquote/views/TutQuotePoLookupView.vue'),
                meta: {breadcrumb: 'PO Lookup', parent: CPTutQuoteRouterPaths.indexPage},
            },
            {
                path: CPTutQuoteRouterPaths.helpPage,
                name: 'help',
                component: () => import('@/apps/website/tutquote/views/TutQuoteHelpView.vue'),
                meta: {breadcrumb: 'Help', parent: CPTutQuoteRouterPaths.indexPage},
            },
        ],
    },
];

const CustomerPortalRouter = createRouter({
    history: createWebHistory(basePath),
    routes: customerPortalRoutes,
    scrollBehavior() {
        return {top: 0, left: 0};
    },
});

export default CustomerPortalRouter;
